import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { showToast } from '../../utils/toast';
import { Button, Field, Textarea, Select, DateInput, ErrorState, Skeleton, useDelayedDelete } from '../../ui';
import './Contacts.css';

// Block 227: the contact record stops being a dead end.
//
// SHAPE: actions first, history second. The census found 12 of 22 contacts have none
// of the eight linked records, so a record of eight empty headings would be worse than
// the dead end it replaces. Every history section renders ONLY when non-empty.
// The actions are always visible, because they are what fills an empty contact.
//
// ONE ROUND TRIP: GET /api/contacts/:id/record returns all of it.
//
// Block D (2026-10-08): A FAILED LOAD IS NOT AN EMPTY RECORD. The old load was
// `r.ok ? r.json() : null` with `.catch(() => {})`, and api() throws on a non-2xx, so the ok
// branch was dead and every failure was swallowed: the record showed nothing, and the Portal panel
// said "No portal yet" about a client whose portal simply had not loaded. Now a failure shows the
// error and a retry, and nothing that depends on the record claims anything until it has loaded.
// The same for the interaction types the Log panel offers.
//
// panel / onPanelChange: the drawer controls which panel is open. Without them the component keeps
// its own state, as before.
//
// THE POP-UP IS A QUICK SUMMARY (Carla, 2026-10-21): Log contact and Portal, and the last five
// history entries with "View all history". Meetings, invoices, proposals, contracts, questionnaires
// and the portal's details moved to the full contact page (ContactPage.jsx), which reads every one of
// them, paged, through the same scopes (GET /api/contacts/:id/documents/:kind). The Email panel that
// lived here, below everything, is the drawer's composer now (ContactComposer.jsx).

export function Section({ title, count, children, action = null }) {
  return (
    <div className="contacts-section">
      <div className="contacts-section__head">
        <div className="contacts-section__title">
          {title}{count != null && count > 0 ? ` · ${count}` : ''}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// What state a contract is actually in, in the words she would use. "signed" and
// "countersigned" are the same word to most readers and mean opposite things
// about whether the job is done. tone: a StatusPill-like weight, as a class.
export function contractState(c) {
  if (c.status === 'countersigned' || (c.clientSignedAt && c.ownerSignedAt)) {
    return { label: 'Complete, signed by both', tone: 'success', downloadable: true };
  }
  if (c.status === 'signed' || c.clientSignedAt) {
    return { label: 'Signed by client, needs your signature', tone: 'pending', downloadable: true };
  }
  if (c.status === 'declined') return { label: 'Declined', tone: 'problem', downloadable: false };
  if (c.status === 'expired')  return { label: 'Expired', tone: 'problem', downloadable: false };
  if (c.status === 'draft')    return { label: 'Draft, not sent', tone: 'muted', downloadable: false };
  return { label: c.sentAt ? 'Sent, awaiting signature' : 'Not sent', tone: 'muted', downloadable: false };
}

export function ContractRow({ c }) {
  const st = contractState(c);
  const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  return (
    <div data-testid="contract-row" className="contacts-rec-row">
      <span className="contacts-rec-row__left">{c.title}</span>
      <span className="contacts-rec-row__right">
        <span className={`contacts-tone contacts-tone--${st.tone}`}>{st.label}</span>
        {st.downloadable && c.token && (
          <a href={`${API}/api/contracts/view/${c.token}/pdf`} target="_blank" rel="noreferrer" className="contacts-link">
            Download
          </a>
        )}
      </span>
    </div>
  );
}

export function Row({ left, right, action = null }) {
  return (
    <div className="contacts-rec-row">
      <span className="contacts-rec-row__left">{left}</span>
      <span className="contacts-rec-row__right contacts-muted">{right}{action}</span>
    </div>
  );
}

export const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
// "proposal_sent" -> "Proposal sent": the whole of what a redacted timeline entry says.
export const eventLabel = (type) => { const t = String(type || 'event').replace(/_/g, ' '); return t.charAt(0).toUpperCase() + t.slice(1); };
// The activity's colour dot, by category (the mockup's "Recent activity" dots). Mirrors the server's
// rule (Aidey Backend/lib/contactActivity.js categoryOf), so a dot and a filter always agree.
export function activityCategory(type) {
  const t = String(type || '');
  if (t === 'email') return 'emails';
  if (t === 'note') return 'notes';
  if (['call', 'meeting', 'message'].includes(t)) return 'logged';
  const families = [['booking_', 'bookings'], ['invoice_', 'invoices'], ['contract_', 'contracts'], ['proposal_', 'proposals'], ['portal_', 'portal'], ['questionnaire_', 'portal']];
  const hit = families.find(([prefix]) => t.startsWith(prefix));
  return hit ? hit[1] : 'other';
}
export const CATEGORY_TINT = {
  emails: 'pastel-blue', bookings: 'pastel-green', invoices: 'pastel-pink', contracts: 'pastel-lilac',
  proposals: 'pastel-yellow', portal: 'pastel-orange', notes: 'pastel-periwinkle', logged: 'pastel-mint', other: 'pastel-neutral',
};
export function ActivityDot({ type }) {
  return <span className="contacts-dot contacts-dot--activity" aria-hidden="true" style={{ '--ui-bg': `var(--${CATEGORY_TINT[activityCategory(type)]})` }} />;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ContactRecord({ contact, onEnablePortal, portalStatus, panel: panelProp, onPanelChange, onState, historyLimit = 5, onViewAll, showHistory = true }) {
  const [record, setRecord] = useState(null);
  const [recordError, setRecordError] = useState(null);
  // Decision 81: a logged entry this person may delete (the server's canDelete), with Undo.
  const historyRemoval = useDelayedDelete({
    send: (item, { keepalive }) => api(`/api/contacts/${item.contactId}/interactions/${item.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (item) => setRecord((rec) => (rec ? { ...rec, interactions: rec.interactions.filter((i) => i.id !== item.id) } : rec)),
    describe: () => 'Entry deleted.',
    failMessage: 'That entry could not be deleted.',
  });
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [typesError, setTypesError] = useState(null);
  const [ownPanel, setOwnPanel] = useState(null);      // null | 'log' | 'portal'
  const controlled = panelProp !== undefined;
  const panel = controlled ? panelProp : ownPanel;
  const setPanel = (p) => { if (controlled) onPanelChange?.(p); else setOwnPanel(p); };
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');
  const stateRef = useRef(onState);
  stateRef.current = onState;

  const load = useCallback(() => {
    if (!contact?.id) return;
    let alive = true;
    setLoading(true);
    setRecordError(null);
    api(`/api/contacts/${contact.id}/record`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        if (!d || !Array.isArray(d.interactions)) throw new Error('The record came back incomplete.');
        setRecord(d);
      })
      .catch((e) => { if (alive) { setRecord(null); setRecordError(messageFromError(e, 'The record could not be loaded.')); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [contact?.id]);

  useEffect(() => load(), [load]);
  useEffect(() => { stateRef.current?.({ record, error: recordError, loading, reload: load }); }, [record, recordError, loading, load]);

  const loadTypes = useCallback(() => {
    // The type list comes from the server, so this component never becomes the registry.
    setTypesError(null);
    api('/api/contacts/interaction-types')
      .then((r) => r.json())
      .then((d) => { if (!Array.isArray(d?.types)) throw new Error('No types came back.'); setTypes(d.types); })
      .catch((e) => setTypesError(messageFromError(e, 'The kinds of contact could not be loaded.')));
  }, []);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  // ── Log contact ────────────────────────────────────────────────────────────
  const [logType, setLogType]   = useState('call');
  const [logSummary, setLogSum] = useState('');
  const [logDate, setLogDate]   = useState(todayISO());
  const selectedType = types.find(t => t.type === logType);

  async function submitLog() {
    setBusy(true); setError('');
    try {
      await api(`/api/contacts/${contact.id}/interactions`, {
        method: 'POST',
        body: JSON.stringify({
          type: logType,
          summary: logSummary,
          // Date only, so a call can be back-dated. Midday avoids a timezone shift
          // pushing the entry onto the previous day.
          occurredAt: new Date(`${logDate}T12:00:00`).toISOString(),
        }),
      });
      setLogSum(''); setPanel(null); showToast('Logged', { type: 'success' });
      load();
    } catch (e) { setError(messageFromError(e, 'Could not log that.')); } finally { setBusy(false); }
  }

  // ── Portal message ─────────────────────────────────────────────────────────
  const [msg, setMsg] = useState('');

  async function sendPortalMessage() {
    setBusy(true); setError('');
    try {
      // This route records its own portal_message interaction, so we do not add one.
      await api(`/api/portal/${record.portal.id}/messages`, {
        method: 'POST', body: JSON.stringify({ body: msg }),
      });
      setMsg(''); setPanel(null); showToast('Message sent', { type: 'success' });
      load();
    } catch (e) { setError(messageFromError(e, 'Could not send.')); } finally { setBusy(false); }
  }

  // The drawer owns the enable call (it also drives its own status line), but the
  // panel has to reload afterwards or it keeps saying "No portal yet" for something
  // that now exists.
  async function enablePortal() {
    try { await onEnablePortal?.(); } finally { load(); }
  }

  const r = record;
  // A QUESTIONNAIRE COUNTS AS HISTORY. Leaving `forms` out would show "no history yet" to a client
  // who has answered one, which is the empty state lying.
  const hasHistory = r && (r.interactions.length || r.tasks?.count || r.meetings?.count ||
    r.invoices?.count || r.proposals?.count || r.contracts?.count || r.forms?.count || r.portal);
  const toggle = (p) => { setError(''); setPanel(panel === p ? null : p); };
  // A panel opens where she is looking: into view, its first field focused (Carla, 2026-10-21: the
  // Email panel opened below Tags, out of sight, and the button looked as if it did nothing).
  const panelRef = useRef(null);
  useEffect(() => {
    if (!panel || !panelRef.current) return;
    panelRef.current.scrollIntoView?.({ block: 'nearest' });
    panelRef.current.querySelector('select, textarea, input, button')?.focus({ preventScroll: true });
  }, [panel, loading]);

  return (
    <div className="contacts-record">

      {/* ── Actions: always visible, on every contact ── */}
      <div className="contacts-record__actions">
        <Button variant="secondary" size="sm" aria-pressed={panel === 'log'} onClick={() => toggle('log')}>Log contact</Button>
        <Button variant="secondary" size="sm" aria-pressed={panel === 'portal'} onClick={() => toggle('portal')}>Portal</Button>
      </div>

      {panel === 'log' && (
        <div className="contacts-panel" ref={panelRef} data-testid="contact-panel-log">
          {typesError ? (
            <ErrorState title="The Log form didn't load" message={typesError} onRetry={loadTypes} />
          ) : (
            <>
              <div className="contacts-panel__pair">
                <Field label="Kind">
                  <Select value={logType} onChange={e => setLogType(e.target.value)}>
                    {types.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
                  </Select>
                </Field>
                <Field label="Date">
                  <DateInput value={logDate} onChange={e => setLogDate(e.target.value)} max={todayISO()} />
                </Field>
              </div>
              <Field label="What happened?">
                <Textarea rows={3} value={logSummary} onChange={e => setLogSum(e.target.value)} />
              </Field>
              {selectedType && !selectedType.touchesLastContacted && (
                <p className="contacts-help">A note is not contact, this will not clear a follow-up reminder.</p>
              )}
              {error && <p className="contacts-error" role="alert">{error}</p>}
              <Button variant="confirm" size="sm" disabled={busy || !types.length} onClick={submitLog}>{busy ? 'Saving…' : 'Save'}</Button>
            </>
          )}
        </div>
      )}

      {panel === 'portal' && (
        <div className="contacts-panel" ref={panelRef} data-testid="contact-panel-portal">
          {loading && !r ? (
            <Skeleton lines={2} label="Loading the portal" />
          ) : recordError ? (
            // Never "No portal yet" about a portal that simply did not load.
            <ErrorState title="The portal status didn't load" message={recordError} onRetry={load} />
          ) : r?.portal ? (
            <>
              <p className="contacts-help">
                Portal {r.portal.isActive ? 'active' : 'inactive'} ·{' '}
                {r.portal.lastAccessedAt ? `last opened ${fmtDate(r.portal.lastAccessedAt)}` : 'never opened'}
                {r.portal.messageCount > 0 ? ` · ${r.portal.messageCount} message${r.portal.messageCount === 1 ? '' : 's'}` : ''}
              </p>
              <Field label="Message your client">
                <Textarea rows={3} value={msg} onChange={e => setMsg(e.target.value)} />
              </Field>
              {error && <p className="contacts-error" role="alert">{error}</p>}
              <Button variant="confirm" size="sm" disabled={busy || !msg.trim()} onClick={sendPortalMessage}>
                {busy ? 'Sending…' : 'Send message'}
              </Button>
            </>
          ) : (
            <>
              <p className="contacts-help">
                No portal yet. {contact.email ? 'Enabling one sends them an invite.' : 'This contact has no email address, so no invite will be sent.'}
              </p>
              <Button variant="confirm" size="sm" disabled={portalStatus === 'loading'} onClick={enablePortal}>
                {portalStatus === 'loading' ? 'Enabling…' : 'Enable portal'}
              </Button>
            </>
          )}
        </div>
      )}

      {loading && !r && !recordError && <Skeleton lines={3} label="Loading this contact's history" />}

      {recordError && (
        <div data-testid="contact-record-failed">
          <ErrorState title="This contact's history didn't load" message={`${recordError} This is not an empty record.`} onRetry={load} />
        </div>
      )}

      {/* ── History: the last few entries, then the full page ── */}
      {r && showHistory && (
        <Section title="Recent activity" action={onViewAll ? (
          <button type="button" className="contacts-section__link" onClick={onViewAll}>View all history</button>
        ) : null}>
          {r.interactions.filter(i => !historyRemoval.isHidden(i.id)).slice(0, historyLimit).map(i => (
            // Decision 30 (TEAM-1b): an entry about an invoice, proposal or contract this person may
            // not open arrives `redacted`: only that it happened, e.g. "Proposal sent", and when.
            i.redacted ? (
              <Row key={i.id} left={<><ActivityDot type={i.type} />{eventLabel(i.type)}</>} right={fmtDate(i.occurredAt)} />
            ) : (
            <Row key={i.id}
              left={<><ActivityDot type={i.type} />{i.summary || i.type}{' '}<span className="contacts-muted">· {i.type.replace(/_/g, ' ')}</span></>}
              right={fmtDate(i.occurredAt)}
              action={i.canDelete ? (
                <button type="button" className="contacts-rec-x" aria-label={`Delete entry: ${i.summary || i.type}`}
                  onClick={() => historyRemoval.remove({ ...i, contactId: contact.id })}>
                  <span aria-hidden="true">✕</span>
                </button>
              ) : null} />
            )
          ))}
          {r.interactions.length === 0 && (
            <p className="contacts-help">
              {hasHistory
                ? 'Nothing logged yet. Their meetings, invoices and documents are on the full profile.'
                : 'Nothing recorded yet. Log a call, send an email, or open a portal and it will appear here.'}
            </p>
          )}
        </Section>
      )}
    </div>
  );
}
